/**
 * Web Mercator projection of a lat/lon onto a georeferenced static-map snapshot.
 *
 * This mirrors the iOS `MapProjection` math (RespAiCommand MapTab.swift): the
 * Google static snapshot is a Web Mercator tile, so x is linear in longitude and
 * y is linear in `mercatorY(lat)`. Given the snapshot's geographic bounds, a
 * coordinate maps to a fractional position in the image — which the web overlay
 * turns into CSS `left`/`top` percentages to drop a dot exactly where a
 * firefighter stood. The snapshot is square (640×640) and rendered at its native
 * aspect, so there is no letterboxing to correct for here (unlike the iOS
 * `scaledToFit` path); a square container makes the projection 1:1.
 */

export type GeoBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type LatLng = { latitude: number; longitude: number };

/**
 * Web Mercator y for a latitude in degrees, clamped to the projection's valid
 * range (~±85.05°). Larger value = further north.
 */
export function mercatorY(latitudeDeg: number): number {
  const clamped = Math.min(Math.max(latitudeDeg, -85.05112878), 85.05112878);
  return Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
}

/**
 * Fractional position of `coord` within a snapshot spanning `bounds`, as
 * `{ x, y }` in the unit square (0,0 = top-left = north-west). Returns `null`
 * when the bounds are degenerate (zero width/height) so callers can skip the
 * dot rather than divide by zero. The result is clamped to [0, 1] so a fix that
 * lands just off the snapshot pins to the edge instead of overflowing the image.
 */
export function projectToFraction(
  coord: LatLng,
  bounds: GeoBounds,
): { x: number; y: number } | null {
  const mercWidth = ((bounds.east - bounds.west) * Math.PI) / 180;
  const mercHeight = mercatorY(bounds.north) - mercatorY(bounds.south);
  if (mercWidth <= 0 || mercHeight <= 0) return null;
  const xFrac = ((coord.longitude - bounds.west) * Math.PI) / 180 / mercWidth;
  const yFrac = (mercatorY(bounds.north) - mercatorY(coord.latitude)) / mercHeight;
  return {
    x: Math.min(Math.max(xFrac, 0), 1),
    y: Math.min(Math.max(yFrac, 0), 1),
  };
}
